import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';
import React from 'react';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import { EDITING_OPTIONS, ENTITLEMENTS } from '../application-core/constants';
import ActionText from '../core/ActionText';
import AddButton from '../core/AddButton';
import BasicLanguageFieldDisplay from '../core/BasicLanguageFieldDisplay';
import DatatypeDisplay from '../core/DatatypeDisplay';
import Errored from '../core/Errored';
import LabelledSwitch from '../core/LabelledSwitch';
import Notification from '../core/Notification';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { BN_NAMESPACE, MAX_DESCRIPTION_LENGTH, STATUS_ACCEPTED, STATUS_REJECTED } from '../core/utils/constants';
import { checkResourceEntitlements, getArrayValues, getGraphFromProject, isInContextComponent, makeCancelable } from '../core/utils/utils';
import Warning from '../core/Warning';
import withGS from '../core/withGShoc';
import WorkflowResourceManager from '../core/workflow/WorkflowResourceManager';
import GS from '../grafsync/grafsync';
import { EXPANDED_NOTE_FIELDS, MAX_DEFINITION_LENGTH } from '../vocabulary/constants';
import { getPairwiseValues } from '../vocabulary/utils/utils';
import AdditionalDetail from './AdditionalDetail';
import BlankNodeHandler from './BlankNodeHandler';
import { MAX_CLASSNAME_LENGTH } from './constants';
import DocumentSource from './DocumentSource';
import FindResourceDialog from './FindResourceDialog';
import ResourceDropRelationshipsDisplay from './ResourceDropRelationshipsDisplay';
import ResourceRelationshipsDisplay from './ResourceRelationshipsDisplay';
import Visibility from './Visibility';

const properties = [
    { rel: 'subPropertyOf', title: "Is Subproperty Of", allowExternal: true, expressions: true, searchClass: "http://www.w3.org/2002/07/owl#ObjectProperty" },
    { rel: 'hasSubProperty', title: "Subproperties", searchClass: "http://www.w3.org/2002/07/owl#ObjectProperty" },
    { rel: 'inverseOf', title: "Inverse Of", allowExternal: true, searchClass: "http://www.w3.org/2002/07/owl#ObjectProperty" },
    { rel: 'propertyDisjointWith', title: "Disjoint With", allowExternal: true, expressions: true, searchClass: "http://www.w3.org/2002/07/owl#ObjectProperty" },
    { rel: 'equivalentProperty', title: "Equivalent Property", allowExternal: true, expressions: true, searchClass: "http://www.w3.org/2002/07/owl#ObjectProperty" }
];

const classProperties = [
    {
        rel: 'domain',
        title: "Domain",
        restrictionProperty: true,
        expressions: true,
        searchClass: "http://www.w3.org/2002/07/owl#Class"
    },
    {
        rel: 'range',
        title: "Range",
        restrictionProperty: true,
        expressions: true,
        searchClass: "http://www.w3.org/2002/07/owl#Class"
    }
];

class ObjectPropertyDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            warning: "",
            isErrored: null
        };
        this.cancellable = null;
    }

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewResource = (property, propertyGraphIRI, suppliedPropertyType) => (iri) => {
        GS.loadData([iri], {}, this.props.graph).then(() => GS.addProperty(this.props.id, this.props.graph, property, iri, null, null, propertyGraphIRI, suppliedPropertyType));
    };

    addDefinedBy = () => GS.addProperty(this.props.id, this.props.graph, "isDefinedBy", this.props.ontologyIRI);

    onUpdate = (property, lang, index, propertyGraphIRI) => (value) => this.props.onUpdate(property, value, lang, index, propertyGraphIRI);

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) => this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    toggleClass = (alias) => () => {
        if (this.props.data.type.includes(alias)) {
            this.onDeleteClass(alias);
        }
        else {
            this.onAddClass(alias);
        }
    };

    onAddClass = (value) => this.props.onAdd("type", value);

    onDeleteClass = (value) => this.props.onDelete("type", value);

    onExitWarning = () => this.setState({ warning: "" });

    doPath(payload) {
        this.cancellable = makeCancelable(GS.path(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    checkProperty = async (id, property) => {
        const { data } = this.props;
        if (this.props.id === id) return "You cannot add relations where a property is related to itself.";
        if (data.propertyDisjointWith && data.propertyDisjointWith.includes(id)) {
            return "You cannot add a property that is already disjoint.";
        }
        if (data.equivalentProperty && data.equivalentProperty.includes(id)) {
            return "You cannot add a property that is already an equivalent property.";
        }
        if (data.inverseOf
            && ((Array.isArray(data.inverseOf) && data.inverseOf.includes(id)) || data.inverseOf === id)) {
            return "You cannot add a property that is already an inverse.";
        }
        if (data.subPropertyOf && data.subPropertyOf.includes(id)) {
            return "You cannot add a property that is already a subproperty of.";
        }
        if (data.hasSubProperty && data.hasSubProperty.includes(id)) {
            return "You cannot add a property that is already a subproperty.";
        }
        const subPropertyOf = await this.doPath({
            graph: getGraphFromProject(this.props.project),
            source: this.props.id,
            path: "http://www.w3.org/2000/01/rdf-schema#subPropertyOf"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return null;
        if (subPropertyOf.results) {
            if (subPropertyOf.results.some(result => result.parent === id)) {
                return "You cannot add a property that is already above in the property hierarchy.";
            }
        }
        const hasSubProperty = await this.doPath({
            graph: getGraphFromProject(this.props.project),
            source: this.props.id,
            path: "https://graphifi.com/schema/xowl/hasSubProperty"
        }).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return null;
        if (hasSubProperty.results) {
            if (hasSubProperty.results.some(result => result.parent === id)) {
                return "You cannot add a property that is already lower in the property hierarchy.";
            }
        }
        return null;
    };

    moveProperty = (property) => (id) => {
        if (this.props.data[property] && this.props.data[property].includes(id)) return;
        this.checkProperty(id, property)
            .then(result => {
                if (result === null) this.addNewResource(property)(id);
                else {
                    if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ warning: result });
                }
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    addChainedProperty = (iri) => GS.insert(this.props.id, this.props.graph, "propertyChainAxiom", iri);

    deleteListItem = (listProp, position, iri) => GS.remove(iri, this.props.graph, listProp, position);

    render() {

        const { data, classes, defaultLang, project, ontologyIRI, buildLink, graph, viewLang, excludeDeprecated,
            hiddenProps, id, availableAnnotationProperty, editingOptions, projectUsers, useWorkflow, entitlements } = this.props;
        const { warning, isErrored } = this.state;

        if (data._notAvailable) return null;
        if (warning !== "") return <Warning message={warning} onExitWarning={this.onExitWarning} />;
        if (isErrored) return <Errored data={isErrored} />;
        if (data.pendingReplacement) return <ActionText>This property has been replaced. Select again to view new data.</ActionText>;

        const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_ONTOLOGY)
            || (data.deprecated === true)
            || (useWorkflow && "status" in data && (data.status[0] === STATUS_ACCEPTED || data.status[0] === STATUS_REJECTED));

        const hidden = hiddenProps ?
            hiddenProps.map(prop => prop.substring(36))
            :
            [];
        const visibleNoteFields = EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property));
        const av = editingOptions && editingOptions.includes(EDITING_OPTIONS.ADVANCED_VIEW);
        const isDefinedBy = getArrayValues(data, "isDefinedBy");

        return (
            <div>
                <ProjectConsumer>
                    {({ id: projectIRI, projectLang, hasComponent }) => (
                        <form autoComplete="off" className={classes.selection}>
                            {data.pendingDeletion &&
                                <Notification error={true} text="This property is pending deletion." dataTest="pending-deletion" />
                            }
                            {data.deprecated &&
                                <div>
                                    <Notification text="This property is deprecated." dataTest="deprecated" />
                                    {
                                        excludeDeprecated && data.subPropertyOf && data.subPropertyOf.includes("http://www.w3.org/2002/07/owl#topObjectProperty") &&
                                        <Notification text="This property is hidden at the top level of the hierarchy." dataTest="deprecated-hidden-note" />
                                    }
                                </div>
                            }
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={!isInContextComponent(data, ontologyIRI) || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            resourceClass="http://www.w3.org/2002/07/owl#ObjectProperty"
                                            componentIRI={ontologyIRI}
                                            entitlements={entitlements}
                                            defaultLang={defaultLang}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={project}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item xs={12} lg={6}>
                                    <BasicLanguageFieldDisplay
                                        dataTest="resource-label"
                                        message={"Add a Label"}
                                        defaultLang={viewLang || defaultLang}
                                        title="Label"
                                        property="label"
                                        values={data.label}
                                        projectLang={projectLang}
                                        onePerLang={true}
                                        graph={graph}
                                        placeholder="Enter label text"
                                        name="Label"
                                        maxLength={MAX_CLASSNAME_LENGTH}
                                        onDelete={this.onDelete}
                                        onUpdate={this.onUpdate}
                                        isReadOnly={isReadOnly}
                                        onAddLanguage={this.addNewLiteral} />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <TitledPanel dataTest="isDefinedBy" height="full" title="Is Defined By"
                                        actions={!isReadOnly && isDefinedBy.length === 0 ?
                                            <AddButton onClick={this.addDefinedBy} dataTest="add-isdefined-by" title="Make defined by this ontology" />
                                            :
                                            null
                                        }>
                                        {isDefinedBy.map(idb =>
                                            <CommonIriDisplay
                                                id={idb}
                                                key={idb}
                                                components={hasComponent}
                                                projectIRI={project}
                                                handleNotAvailable={true}
                                                property="isDefinedBy"
                                                isReadOnly={true}
                                                compact={true}
                                                graph={graph}
                                                source={id}
                                                wrap={false}
                                                viewLang={viewLang}
                                                defaultLang={defaultLang} />
                                        )}
                                    </TitledPanel>
                                </Grid>
                                {
                                    properties.map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            :
                                            [];
                                        const filteredItemData = itemData.filter(item => item !== "http://www.w3.org/2002/07/owl#topObjectProperty");
                                        const ebp = [];
                                        ebp.push({
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#equivalentProperty"
                                            ],
                                            target: data.id
                                        });
                                        ebp.push({
                                            predicate: [
                                                "http://www.w3.org/2002/07/owl#propertyDisjointWith"
                                            ],
                                            target: data.id
                                        });
                                        ebp.push({
                                            predicate: [
                                                "http://www.w3.org/2000/01/rdf-schema#subPropertyOf"
                                            ],
                                            target: data.id,
                                            reverse: true
                                        });
                                        ebp.push({
                                            predicate: [
                                                "https://graphifi.com/schema/xowl/hasSubProperty"
                                            ],
                                            target: data.id,
                                            reverse: true
                                        });
                                        if (item.rel === "subPropertyOf") {
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2000/01/rdf-schema#subPropertyOf"
                                                ],
                                                target: data.id,
                                                pathOperators: ["+"]
                                            });
                                        }
                                        if (item.rel === "hasSubProperty") {
                                            ebp.push({
                                                predicate: [
                                                    "https://graphifi.com/schema/xowl/hasSubProperty"
                                                ],
                                                target: data.id,
                                                pathOperators: ["+"]
                                            });
                                        }
                                        let excludeSearchClass = [];
                                        if (item.rel === "inverseOf") {
                                            if (data.type.includes("SymmetricProperty")) excludeSearchClass.push("http://www.w3.org/2002/07/owl#AsymmetricProperty");
                                            if (data.type.includes("AsymmetricProperty")) excludeSearchClass.push("http://www.w3.org/2002/07/owl#SymmetricProperty");
                                        }
                                        let exclude = [];
                                        if (data.inverseOf) exclude = exclude.concat(data.inverseOf);
                                        if (data.equivalentProperty) exclude = exclude.concat(data.equivalentProperty);
                                        if (data.propertyDisjointWith) exclude = exclude.concat(data.propertyDisjointWith);
                                        if (data.subPropertyOf) exclude = exclude.concat(data.subPropertyOf);
                                        if (data.hasSubProperty) exclude = exclude.concat(data.hasSubProperty);
                                        exclude = [...new Set(exclude)];
                                        return <Grid item xs={12} lg={6} key={"rel-" + index}>
                                            <ResourceDropRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                onMove={this.moveProperty(item.rel)}
                                                property={item.rel}
                                                graph={graph}
                                                addNewResource={this.addNewResource(item.rel)}
                                                title={item.title}
                                                excludeByPath={ebp}
                                                useWorkflow={useWorkflow}
                                                components={hasComponent}
                                                expressions={av && item.expressions}
                                                permitExternal={item.allowExternal}
                                                permittedExternalType={item.allowExternal ? "ObjectProperty" : null}
                                                view="objectProperty"
                                                buildLink={buildLink}
                                                searchExcludeClass={excludeSearchClass}
                                                searchClass={item.searchClass}
                                                exclude={exclude}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={filteredItemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    })
                                }
                                {av &&
                                    <Grid item md={12} lg={6}>
                                        <TitledPanel dataTest="propertyChainAxiom" height="full" title="Property Chain Axiom" actions={
                                            !isReadOnly ?
                                                <FindResourceDialog
                                                    key={viewLang}
                                                    permitExternal={false}
                                                    components={hasComponent}
                                                    exclude={[]}
                                                    useWorkflow={useWorkflow}
                                                    defaultLang={defaultLang}
                                                    sourceComponent={ontologyIRI}
                                                    permittedExternalType="ObjectProperty"
                                                    buttonTitle="Add a property"
                                                    title="Add Property"
                                                    searchClass="http://www.w3.org/2002/07/owl#ObjectProperty"
                                                    project={projectIRI}
                                                    graph={graph}
                                                    viewLang={viewLang}
                                                    onAddResource={this.addChainedProperty} />
                                                :
                                                null
                                        }>
                                            {data.propertyChainAxiom && data.propertyChainAxiom.length === 1 &&
                                                <div style={{ marginBottom: 12 }}>
                                                    <Notification text="There should be at least two items." dataTest="at-least-two" />
                                                </div>
                                            }
                                            {data.propertyChainAxiom && data.propertyChainAxiom.map((item, itemIndex) => {
                                                return item.startsWith(BN_NAMESPACE) ?
                                                    <div
                                                        key={item + "-" + itemIndex}
                                                        style={{ marginBottom: 12 }}>
                                                        <BlankNodeHandler
                                                            id={item}
                                                            key={item + "-" + itemIndex}
                                                            graph={graph}
                                                            useWorkflow={useWorkflow}
                                                            context={ontologyIRI}
                                                            handleNotAvailable={true}
                                                            projectIRI={projectIRI}
                                                            sourceResource={id}
                                                            sourceComponent={ontologyIRI}
                                                            components={hasComponent}
                                                            isReadOnly={isReadOnly}
                                                            viewLang={viewLang}
                                                            defaultLang={defaultLang} />
                                                    </div>
                                                    :
                                                    <CommonIriDisplay
                                                        id={item}
                                                        key={item + "-" + itemIndex}
                                                        onDeleteIRI={() => this.deleteListItem("propertyChainAxiom", itemIndex, id)}
                                                        components={hasComponent}
                                                        projectIRI={projectIRI}
                                                        handleNotAvailable={true}
                                                        property="propertyChainAxiom"
                                                        context={ontologyIRI}
                                                        contextProperty="isDefinedBy"
                                                        isReadOnly={isReadOnly}
                                                        compact={true}
                                                        graph={graph}
                                                        source={id}
                                                        wrap={true}
                                                        viewLang={viewLang}
                                                        defaultLang={defaultLang} />;
                                            })}
                                        </TitledPanel>
                                    </Grid>
                                }
                                {
                                    classProperties.map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ?
                                                data[item.rel]
                                                :
                                                [data[item.rel]])
                                            :
                                            [];
                                        const ebp = [];
                                        const spValues = GS.getSuperPropertyValues(data.id, graph, item.rel);
                                        spValues.forEach(item => {
                                            // Existing item disjoint with result
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2002/07/owl#disjointWith",
                                                    "https://graphifi.com/schema/xowl/hasSubClass"
                                                ],
                                                target: item,
                                                reverse: true,
                                                pathOperators: ["", "*"]
                                            });
                                            // Result disjoint with existing item
                                            ebp.push({
                                                predicate: [
                                                    "http://www.w3.org/2000/01/rdf-schema#subClassOf",
                                                    "http://www.w3.org/2002/07/owl#disjointWith"
                                                ],
                                                target: item,
                                                pathOperators: ["*", ""]
                                            });
                                        });
                                        return <Grid item xs={12} lg={6} key={"rel-" + index}>
                                            <ResourceRelationshipsDisplay
                                                dataTest={item.rel}
                                                project={project}
                                                property={item.rel}
                                                graph={graph}
                                                includeSystemGraph={true}
                                                addRestriction={this.addNewRestriction}
                                                restrictionProperty={av && item.restrictionProperty}
                                                expressions={av && item.expressions}
                                                addNewResource={this.addNewResource(item.rel)}
                                                title={item.title}
                                                excludeByPath={ebp}
                                                view="class"
                                                components={hasComponent}
                                                buildLink={buildLink}
                                                searchClass={item.searchClass}
                                                source={data.id}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                ontologyIRI={ontologyIRI}
                                                values={itemData}
                                                isReadOnly={isReadOnly} />
                                        </Grid>;
                                    })
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#comment") &&
                                    <Grid item xs={12} lg={6}>
                                        <BasicLanguageFieldDisplay
                                            dataTest="comments"
                                            message={"Add a comment"}
                                            graph={graph}
                                            defaultLang={viewLang || defaultLang}
                                            placeholder="Enter comment text"
                                            title="Comment"
                                            property="comment"
                                            values={data.comment}
                                            maxLength={MAX_DESCRIPTION_LENGTH}
                                            projectLang={projectLang}
                                            multiline={true}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onUpdate}
                                            isReadOnly={isReadOnly}
                                            onAddLanguage={this.addNewLiteral} />
                                    </Grid>
                                }
                                {!hiddenProps.includes("http://www.w3.org/2000/01/rdf-schema#seeAlso") &&
                                    [
                                        {
                                            rel: 'seeAlso',
                                            title: "See Also"
                                        }
                                    ].map((item, index) => {
                                        const itemData = data[item.rel] ?
                                            (Array.isArray(data[item.rel]) ? data[item.rel] : [data[item.rel]])
                                            : [];
                                        return <Grid item xs={12} lg={6} key={"rel-" + index}>
                                            <DatatypeDisplay
                                                title={item.title}
                                                source={data.id}
                                                datatype={""}
                                                defaultLang={defaultLang}
                                                viewLang={viewLang}
                                                projectLang={projectLang}
                                                property={item.rel}
                                                values={itemData}
                                                useWorkflow={useWorkflow}
                                                onDelete={this.onDelete}
                                                context={ontologyIRI}
                                                onUpdate={this.onUpdate}
                                                projectIRI={projectIRI}
                                                noRangeOptions={{
                                                    language: false,
                                                    dateTime: false,
                                                    datatype: true,
                                                    boolean: false,
                                                    excludeDatatypes: ["INTEGER", "NNINTEGER", "DECIMAL", "LONG", "FLOAT", "DATE", "DOUBLE"]
                                                }}
                                                graph={graph}
                                                associatedProjectGraph={graph}
                                                isReadOnly={isReadOnly}
                                                canAddValues={true}
                                                addNewResource={this.addNewResource(item.rel, graph, "@id")}
                                                onAddLiteral={this.addNewLiteral(item.rel)}
                                                onAddTypedLiteral={this.addNewTypedLiteral(item.rel)}
                                            />
                                        </Grid>;
                                    })
                                }
                                <Grid item xs={12} lg={6}>
                                    <TitledPanel height="full" title="Characteristics">
                                        {data.type.includes("FunctionalProperty") && data.type.includes("TransitiveProperty") &&
                                            <Notification text="Please check whether being both functional and transitive is intended." dataTest="functional-transitive" />
                                        }
                                        {data.type.includes("ReflexiveProperty") && data.type.includes("IrreflexiveProperty") &&
                                            <Notification error={true} text="Property is both reflexive and irreflexive." dataTest="reflexive-irreflexive" />
                                        }
                                        {data.type.includes("SymmetricProperty") && data.type.includes("AsymmetricProperty") &&
                                            <Notification error={true} text="Property is both symmetric and asymmetric." dataTest="symmetric-asymmetric" />
                                        }
                                        <Grid container>
                                            <Grid item xs={12} lg={6}>
                                                <LabelledSwitch
                                                    dataTest="functional"
                                                    disabled={isReadOnly}
                                                    isReadOnly={isReadOnly}
                                                    label="Functional"
                                                    checked={data.type.includes("FunctionalProperty")}
                                                    handleChange={this.toggleClass("FunctionalProperty")}
                                                />
                                                <LabelledSwitch
                                                    dataTest="inversefunctional"
                                                    disabled={isReadOnly}
                                                    isReadOnly={isReadOnly}
                                                    label="Inverse Functional"
                                                    checked={data.type.includes("InverseFunctionalProperty")}
                                                    handleChange={this.toggleClass("InverseFunctionalProperty")}
                                                />
                                                <LabelledSwitch
                                                    dataTest="symmetric"
                                                    disabled={isReadOnly || (!data.type.includes("SymmetricProperty") && data.type.includes("AsymmetricProperty"))}
                                                    isReadOnly={isReadOnly}
                                                    label="Symmetric"
                                                    checked={data.type.includes("SymmetricProperty")}
                                                    handleChange={this.toggleClass("SymmetricProperty")}
                                                />
                                                <LabelledSwitch
                                                    dataTest="asymmetric"
                                                    disabled={isReadOnly || (!data.type.includes("AsymmetricProperty") && data.type.includes("SymmetricProperty"))}
                                                    isReadOnly={isReadOnly}
                                                    label="Asymmetric"
                                                    checked={data.type.includes("AsymmetricProperty")}
                                                    handleChange={this.toggleClass("AsymmetricProperty")}
                                                />
                                            </Grid>
                                            <Grid item xs={12} lg={6}>
                                                <LabelledSwitch
                                                    dataTest="transitive"
                                                    disabled={isReadOnly}
                                                    isReadOnly={isReadOnly}
                                                    label="Transitive"
                                                    checked={data.type.includes("TransitiveProperty")}
                                                    handleChange={this.toggleClass("TransitiveProperty")}
                                                />
                                                <LabelledSwitch
                                                    dataTest="reflexive"
                                                    disabled={isReadOnly || (!data.type.includes("ReflexiveProperty") && data.type.includes("IrreflexiveProperty"))}
                                                    isReadOnly={isReadOnly}
                                                    label="Reflexive"
                                                    checked={data.type.includes("ReflexiveProperty")}
                                                    handleChange={this.toggleClass("ReflexiveProperty")}
                                                />
                                                <LabelledSwitch
                                                    dataTest="irreflexive"
                                                    disabled={isReadOnly || (!data.type.includes("IrreflexiveProperty") && data.type.includes("ReflexiveProperty"))}
                                                    isReadOnly={isReadOnly}
                                                    label="Irreflexive"
                                                    checked={data.type.includes("IrreflexiveProperty")}
                                                    handleChange={this.toggleClass("IrreflexiveProperty")}
                                                />
                                            </Grid>
                                        </Grid>
                                    </TitledPanel>
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <Visibility
                                        graph={graph}
                                        viewLang={viewLang}
                                        componentIRI={ontologyIRI}
                                        source={id}
                                        sourceType={data.type}
                                        defaultLang={defaultLang}
                                        sources={data.visibility} />
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    <DocumentSource
                                        dataTest="document-source-list"
                                        graph={graph}
                                        componentIRI={ontologyIRI}
                                        viewLang={viewLang}
                                        defaultLang={defaultLang}
                                        sources={data.documentSource} />
                                </Grid>
                            </Grid>
                            {visibleNoteFields.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>SKOS Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        {EXPANDED_NOTE_FIELDS.filter(field => !hidden.includes(field.property)).map(field => {
                                            let existingValues = undefined;
                                            if (field.pairwise) {
                                                existingValues = getPairwiseValues(data);
                                            }
                                            return <Grid key={field.title} item xs={12} lg={6}>
                                                <BasicLanguageFieldDisplay
                                                    dataTest={field.property}
                                                    message={"Add " + field.message}
                                                    defaultLang={viewLang || defaultLang}
                                                    title={field.title}
                                                    required={false}
                                                    onePerLang={field.onePerLang === true}
                                                    property={field.property}
                                                    values={data[field.property]}
                                                    existingValues={existingValues}
                                                    maxLength={field.maxLength || MAX_DEFINITION_LENGTH}
                                                    projectLang={projectLang}
                                                    placeholder={field.placeholder}
                                                    name={field.message}
                                                    onDelete={this.onDelete}
                                                    onUpdate={this.onUpdate}
                                                    isReadOnly={isReadOnly}
                                                    multiline={field.multiline}
                                                    onAddLanguage={this.addNewLiteral} />
                                            </Grid>;
                                        }
                                        )}
                                    </Grid>
                                </React.Fragment>
                            }
                            {availableAnnotationProperty && availableAnnotationProperty.length > 0 &&
                                <React.Fragment>
                                    <Typography
                                        data-test="annotation-props-title"
                                        className={classes.subMainHeading}
                                        style={{ marginTop: '20px' }}>Additional Annotations</Typography>
                                    <Grid data-test="additional-annotations" container spacing={1} classes={
                                        {
                                            "spacing-xs-2": this.props.classes.fullGrid
                                        }
                                    }>
                                        <AdditionalDetail
                                            data={data}
                                            projectIRI={project}
                                            viewLang={viewLang}
                                            defaultLang={defaultLang}
                                            projectLang={projectLang}
                                            graph={graph}
                                            useWorkflow={useWorkflow}
                                            ontologyIRI={ontologyIRI}
                                            isReadOnly={isReadOnly}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onUpdate}
                                            context={ontologyIRI}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewResource={this.addNewResource}
                                            availableAnnotationProperty={availableAnnotationProperty} />
                                    </Grid>
                                </React.Fragment>
                            }
                        </form>
                    )}
                </ProjectConsumer>
                <div data-test="info-panel-bottom" >&#160;</div>
            </div>
        );
    }
}

ObjectPropertyDetail.defaultProps = {
    hiddenProps: [],
    useWorkflow: false
};

ObjectPropertyDetail.propTypes = {
    classes: PropTypes.object,
    id: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    editingOptions: PropTypes.array,
    projectUsers: PropTypes.array,
    useWorkflow: PropTypes.bool,
    entitlements: PropTypes.array.isRequired,
    defaultLang: PropTypes.string.isRequired,
    buildLink: PropTypes.func.isRequired,
    viewLang: PropTypes.string,
    excludeDeprecated: PropTypes.bool,
    hiddenProps: PropTypes.array,
    project: PropTypes.string.isRequired,
    ontologyIRI: PropTypes.string.isRequired,
    availableAnnotationProperty: PropTypes.array,
    onDelete: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired
};

export default withStyles(styles)(withGS(ObjectPropertyDetail, ["ALL"], null, {}, true));